import React, {useEffect, useRef, useState} from 'react';
import {ActivityIndicator, View, FlatList, StyleSheet} from 'react-native';

import useStores from '../../stores/useStores';
import {observer} from 'mobx-react';
import _ from 'lodash';

import {RNEButton} from '../../components';
import {colorPalette} from '../../../core/config/color.config';

import {ConsultationRequestResBody} from '../../server/types/consultation.types';
import ConsultationMessageBubble from './ConsultationMessageBubble';
import ConsultationMessageAttachment from './ConsultationMessageAttachment';
import {useGetOngoingConsultation} from '../../server/react-query/useConsultation';
import {ProcedureStatusEnum} from '../../server/types/procedure.types';
import {ConsultationInformationMessage} from '..';

interface ConsultationMessagesProps {
  conversation: any;
  serviceRequest: ConsultationRequestResBody;
  onStartGenerating: () => void;
  onStopGenerating: () => void;
}

const ConsultationMessages = ({
  conversation,
  serviceRequest,
  onStartGenerating,
  onStopGenerating,
}: ConsultationMessagesProps) => {
  const {
    authStore,
    patientProfileStore,
    doctorProfileStore,
    conversationStore,
  } = useStores();

  const [messages, setMessages] = useState<any[]>([]);
  const [isPatient, setIsPatient] = useState(false);
  const [authorId, setAuthorId] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const flatListRef = useRef<FlatList>(null);

  const {refetch: refetchOngoingConsultationsList} = useGetOngoingConsultation({
    status: [ProcedureStatusEnum.ONGOING],
    subject: patientProfileStore.activeProfile?.patientId,
    SortBy: 'noteTime',
    Ascending: false,
  });

  const scrollToBottom = () => {
    if (flatListRef.current) {
      flatListRef.current.scrollToEnd({animated: true});
    }
  };

  const getMessages = async () => {
    if (conversation) {
      setIsLoading(true);
      try {
        const msgs = await conversation.getMessages(1000);
        setMessages(msgs.items);

        setTimeout(() => scrollToBottom(), 200);
      } catch (error) {
        // handle errors
      }
      setIsLoading(false);
    }
  };

  const onMessageAdded = (msg: any) => {
    setMessages(old => [...old, msg]);

    setTimeout(() => {
      scrollToBottom();
    }, 150);

    refetchOngoingConsultationsList();
  };

  useEffect(() => {
    setMessages([]);

    if (conversation) {
      conversation.on('messageAdded', onMessageAdded);

      conversation.on('messageUpdated', ({message}) => {
        setMessages(prev =>
          prev.map(m => (m.sid === message.sid ? message : m)),
        );
        setTimeout(scrollToBottom, 120);
      });

      getMessages();
    }

    return () => {
      conversation?.removeAllListeners?.('messageAdded');
      conversation?.removeAllListeners?.('messageUpdated');
    };
  }, [conversation]);

  useEffect(() => {
    setIsPatient(authStore.userRole === 'patient');
  }, [authStore.userRole]);

  useEffect(() => {
    let id = '';

    if (isPatient) {
      id = patientProfileStore.activeProfile?.patientId || '';
    } else {
      id = doctorProfileStore.doctorProfile?.practitionerId || '';
    }

    setAuthorId(id);
  }, [
    isPatient,
    patientProfileStore.activeProfile,
    doctorProfileStore.doctorProfile,
  ]);

  const renderMessage = ({item: message}: any) => {
    if (_.isEmpty(message.attributes)) {
      return (
        <ConsultationMessageBubble
          key={message.sid}
          message={message.body}
          patient={message.patient}
          timestamp={message.dateUpdated}
          owner={message.author === authorId}
          onStartGenerating={onStartGenerating}
          onStopGenerating={onStopGenerating}
          isPatient={
            (isPatient && message.author === authorId) ||
            (!isPatient && message.author !== authorId)
          }
          serviceRequest={serviceRequest}
          onReloadChat={() => {
            getMessages();
          }}
          latestVideoCallRequestTimestamp={
            conversationStore.latestVideoCallRequest?.timestamp
          }
        />
      );
    } else {
      return message.attributes.Type === 'invoice' ? (
        <ConsultationInformationMessage
          timestamp={new Date(message.dateUpdated)}
        />
      ) : (
        <ConsultationMessageAttachment
          messageType={message.author === authorId ? 'outgoing' : 'incoming'}
          attribute={message.attributes}
          serviceRequest={serviceRequest}
        />
      );
    }
  };

  return (
    <View style={{flex: 1}}>
      {isLoading ? (
        <View style={styles.contentPlaceholder}>
          <RNEButton height={32} loadingProps={loadingPropsStyle} loading={true} />
        </View>
      ) : (
        <FlatList
          ref={flatListRef}
          data={messages}
          keyExtractor={item => item.sid}
          renderItem={renderMessage}
          onContentSizeChange={() => scrollToBottom()}
          showsVerticalScrollIndicator={false}
        />
      )}
    </View> 
  );
};

const loadingPropsStyle = {
  color: colorPalette.primary,
};

const styles = StyleSheet.create({
  contentPlaceholder: {
    marginTop: 24,
  },
});

export default observer(ConsultationMessages);
